import React from 'react';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { ExpediaIcon } from '@assets/icons';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { AccommodationCard } from '../../Accommodations.types';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { pxToRem } from '@withjoy/joykit/theme';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';

export const HotelPrice = (props: { accommodation: AccommodationCard; showComparativePrice: boolean }) => {
  const { tq } = useTranslation('travelMap');
  const { price, strikeOutPrice, percentageOff, soldOutUrl, loading } = useTravelPlacesPricingAndBookingDetailsFragment(props.accommodation.id, 'accommodationPlace');

  return (
    <SkeletonGroup
      isReady={!loading}
      placeholder={
        <>
          <Flex
            gap={2}
            justifyContent="space-between"
            alignItems={'flex-start'}
          >
            <Skeleton
              placeholder={
                <Box
                  width={'8rem'}
                  typographyVariant={'body1'}
                >
                  &nbsp;
                </Box>
              }
            />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
            >
              <Skeleton
                placeholder={
                  <Box
                    width={'3.5rem'}
                    typographyVariant={'hed2'}
                  >
                    &nbsp;
                  </Box>
                }
              />
            </Box>
          </Flex>
          <Divider
            marginTop={2}
            marginBottom={3}
          />
          <Flex
            gap={2}
            justifyContent="space-between"
            alignItems={'flex-start'}
          >
            <Skeleton
              placeholder={
                <Box
                  width={'8rem'}
                  typographyVariant={'body1'}
                >
                  &nbsp;
                </Box>
              }
            />
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
            >
              <Skeleton
                placeholder={
                  <Box
                    width={'3.5rem'}
                    typographyVariant={'hed2'}
                  >
                    &nbsp;
                  </Box>
                }
              />
            </Box>
          </Flex>
        </>
      }
    >
      {price ? (
        <>
          <Flex
            alignItems="center"
            justifyContent="space-between"
          >
            <TextV2 typographyVariant="body1">{tq`Rates from`}</TextV2>
            <TextV2
              typographyVariant="hed6"
              fontWeight={'semibold'}
              display="flex"
              alignItems="center"
            >
              {percentageOff && (
                <TextV2
                  paddingX={{ _: 2, sm2: 3 }}
                  typographyVariant="label3"
                  marginRight={{ _: 2, sm2: 3 }}
                  paddingY={pxToRem(4)}
                  fontSize={{ _: pxToRem(11), sm2: pxToRem(13) }}
                  borderRadius={'full'}
                  backgroundColor={'accentGreen2'}
                  color={'accentGreen3'}
                  width={'fit-content'}
                  whiteSpace={'nowrap'}
                >
                  {tq`${percentageOff}% off`}
                </TextV2>
              )}
              {price}
            </TextV2>
          </Flex>
          {strikeOutPrice && props.showComparativePrice && (
            <>
              <Divider
                marginTop={2}
                marginBottom={3}
              />
              <Flex
                alignItems="end"
                justifyContent="space-between"
              >
                <TextV2
                  display="flex"
                  typographyVariant="body1"
                  color="#191E3B"
                  alignItems="center"
                  gap={1}
                  marginRight={2}
                >
                  {tq`Compared to`} <ExpediaIcon />
                </TextV2>
                <TextV2
                  typographyVariant="body1"
                  textDecoration="line-through"
                >
                  {strikeOutPrice}
                </TextV2>
              </Flex>
            </>
          )}
        </>
      ) : soldOutUrl ? (
        <SoldOutTag />
      ) : null}
    </SkeletonGroup>
  );
};
